/** @format */

import { FC, useState } from "react";
import Modal from "../ui/Modal";
import { sopService } from "../../api/sopService";
import type { Organization } from "../../types/organization";
import { Loader2 } from "lucide-react";

interface MigrateSopDialogProps {
  open: boolean;
  onClose: () => void;
  sopId: string;
  sopTitle: string;
  currentOrgId: string | undefined;
  organizations: Organization[];
  onSuccess: () => void;
}

export const MigrateSopDialog: FC<MigrateSopDialogProps> = ({
  open,
  onClose,
  sopId,
  sopTitle,
  currentOrgId,
  organizations,
  onSuccess,
}) => {
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [isMigrating, setIsMigrating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const targetOrgs = organizations.filter((o) => o.orgId !== currentOrgId);

  const handleMigrate = async () => {
    if (!selectedOrgId) return;
    setIsMigrating(true);
    setError(null);
    try {
      await sopService.migrateSopToOrganization(sopId, selectedOrgId);
      onSuccess();
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to migrate SOP",
      );
    } finally {
      setIsMigrating(false);
    }
  };

  const handleClose = () => {
    if (!isMigrating) {
      setSelectedOrgId("");
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Migrate SOP to Organization"
      size="small"
    >
      <div className="flex flex-col gap-4">
        <p className="text-slate-300 text-sm">
          Move &quot;{sopTitle}&quot; to a different organization.
        </p>

        <div>
          <label
            htmlFor="migrate-org-select"
            className="block text-sm font-medium text-slate-300 mb-2"
          >
            Target organization
          </label>
          <select
            id="migrate-org-select"
            value={selectedOrgId}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            disabled={isMigrating}
            className="w-full bg-slate-800/80 border border-slate-600/50 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent disabled:opacity-50"
          >
            <option value="">Select organization</option>
            {targetOrgs.map((org) => (
              <option key={org.orgId} value={org.orgId}>
                {org.name}
              </option>
            ))}
          </select>
        </div>

        {targetOrgs.length === 0 && (
          <p className="text-amber-400 text-sm">
            No other organizations available to migrate to.
          </p>
        )}

        {error && (
          <p className="text-red-400 text-sm">{error}</p>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={isMigrating}
            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-300 hover:bg-slate-700/50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleMigrate}
            disabled={!selectedOrgId || isMigrating}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isMigrating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Migrating...
              </>
            ) : (
              "Migrate"
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};
